import React, { useEffect, useMemo, useState } from 'react';
import { Platform, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import carData from '../data/car_database.json';
import { FUEL_OPTIONS } from '../helpers/serviceMode';
import { useServiceMode } from '../helpers/ServiceModeContext';

// Re-exported for back-compat — the canonical list now lives in
// `app/helpers/serviceMode.js` (DEFAULT_CATALOGS.fuelOptions) so it can be
// overridden by an applied service mode.
export { FUEL_OPTIONS };

const BRAND_PRIMARY = '#2854C5';
const IS_WEB = Platform.OS === 'web';

const toNumberString = (val) => (val === null || val === undefined ? '' : String(val));

/** Case/whitespace-insensitive comparison key for brand/model/type names. */
const lower = (val) => String(val ?? '').trim().toLowerCase();

/**
 * De-duplicate a list of option strings case-insensitively (first casing
 * wins) and sort it alphabetically — used for the type/variant suggestions.
 * @param {Array<*>} list Raw option list.
 * @returns {string[]} The cleaned, sorted list.
 */
const uniqueSorted = (list) => {
    const seen = new Map();
    (Array.isArray(list) ? list : []).forEach((item) => {
        const label = String(item ?? '').trim();
        if (label && !seen.has(label.toLowerCase())) {
            seen.set(label.toLowerCase(), label);
        }
    });

    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
};

const Field = ({ label, children, theme }) => (
    <View style={styles.field}>
        <Text style={[styles.label, { color: theme.colors.subText }]}>{label}</Text>
        {children}
    </View>
);

/**
 * Vehicle brand/model/fuel picker used by NewWorkSheetModal and
 * CustomerVehicleScreen. `brands`/`fuelOptions` are optional overrides —
 * default to the bundled `car_database.json` / {@link FUEL_OPTIONS} so every
 * existing caller keeps today's exact behaviour (fail-open). `onAddBrand`,
 * when provided, renders a "+ Add '<query>'" row in the brand dropdown when
 * no bundled/merged brand matches the typed text (service-mode brand
 * quick-add — see ServiceModeContext.addBrandLocal). `onAddModel(brand,
 * model)` / `onAddType(brand, model, type)` do the same for the model and
 * type fields; when omitted they default to the persisting
 * `addModel`/`addType` mutators of {@link useServiceMode}, so a value typed
 * once is offered on the next work order.
 */
export default function VehicleSpecSelector({ value, onChange, theme, tr, brands: brandEntries, fuelOptions, onAddBrand, onAddModel, onAddType, isFieldVisible = () => true }) {
    const [brandFocused, setBrandFocused] = useState(false);
    const [modelFocused, setModelFocused] = useState(false);
    const [typeFocused, setTypeFocused] = useState(false);
    const { addModel: persistModel, addType: persistType } = useServiceMode();

    const addModelHandler = typeof onAddModel === 'function' ? onAddModel : persistModel;
    const addTypeHandler = typeof onAddType === 'function' ? onAddType : persistType;

    const entries = useMemo(() => {
        const source = Array.isArray(brandEntries) && brandEntries.length > 0 ? brandEntries : carData;
        return (source || []).map((entry) => {
            const models = Array.isArray(entry.models) ? entry.models : [];
            // `modelCatalog` ([{name, types}]) is the store-scoped catalogue;
            // older rows / the bundled `car_database.json` only carry the
            // legacy `models: [String]` mirror, so it is projected up.
            const rawCatalog = Array.isArray(entry.modelCatalog) && entry.modelCatalog.length > 0 ? entry.modelCatalog : models;
            const modelCatalog = rawCatalog.map((model) => (typeof model === 'string'
                ? { name: model.trim(), types: [] }
                : { name: String(model?.name || '').trim(), types: Array.isArray(model?.types) ? model.types : [] }))
                .filter((model) => model.name);

            return {
                brand: entry.brand || entry.name || '',
                models,
                modelCatalog,
                types: Array.isArray(entry.types) ? entry.types : [],
            };
        }).filter((entry) => entry.brand);
    }, [brandEntries]);

    const fuels = Array.isArray(fuelOptions) && fuelOptions.length > 0 ? fuelOptions : FUEL_OPTIONS;

    const brands = useMemo(
        () => [...new Set(entries.map((entry) => entry.brand))].sort((a, b) => a.localeCompare(b)),
        [entries],
    );

    const selectedBrandEntry = useMemo(
        () => entries.find((entry) => String(entry.brand || '').toLowerCase() === String(value.brand || '').toLowerCase()),
        [entries, value.brand],
    );

    const modelOptions = useMemo(
        () => uniqueSorted([
            ...(selectedBrandEntry?.models || []),
            ...(selectedBrandEntry?.modelCatalog || []).map((model) => model.name),
        ]),
        [selectedBrandEntry],
    );

    // Types/variants of the selected model, unioned with the brand-level ones
    // (those apply to every model of the brand).
    const typeOptions = useMemo(() => {
        const modelEntry = (selectedBrandEntry?.modelCatalog || []).find((model) => lower(model.name) === lower(value.model));

        return uniqueSorted([...(selectedBrandEntry?.types || []), ...(modelEntry?.types || [])]);
    }, [selectedBrandEntry, value.model]);

    const filteredBrands = useMemo(() => {
        const q = String(value.brand || '').trim().toLowerCase();
        if (!q) return brands.slice(0, 18);
        return brands.filter((brand) => brand.toLowerCase().includes(q)).slice(0, 18);
    }, [brands, value.brand]);

    const showAddBrandRow = useMemo(() => {
        const q = String(value.brand || '').trim();
        if (!q || typeof onAddBrand !== 'function') return false;
        return !brands.some((brand) => brand.toLowerCase() === q.toLowerCase());
    }, [value.brand, brands, onAddBrand]);

    const filteredModels = useMemo(() => {
        const q = String(value.model || '').trim().toLowerCase();
        if (!q) return modelOptions.slice(0, 18);
        return modelOptions.filter((model) => model.toLowerCase().includes(q)).slice(0, 18);
    }, [modelOptions, value.model]);

    const showAddModelRow = useMemo(() => {
        const q = String(value.model || '').trim();
        if (!q || !value.brand || typeof addModelHandler !== 'function') return false;
        return !modelOptions.some((model) => lower(model) === lower(q));
    }, [value.model, value.brand, modelOptions, addModelHandler]);

    const filteredTypes = useMemo(() => {
        const q = String(value.type || '').trim().toLowerCase();
        if (!q) return typeOptions.slice(0, 18);
        return typeOptions.filter((type) => type.toLowerCase().includes(q)).slice(0, 18);
    }, [typeOptions, value.type]);

    const showAddTypeRow = useMemo(() => {
        const q = String(value.type || '').trim();
        if (!q || !value.brand || typeof addTypeHandler !== 'function') return false;
        return !typeOptions.some((type) => lower(type) === lower(q));
    }, [value.type, value.brand, typeOptions, addTypeHandler]);

    useEffect(() => {
        if (!selectedBrandEntry && value.model) {
            onChange({ ...value, model: '' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBrandEntry]);

    const setField = (key, nextValue) => {
        const next = { ...value, [key]: nextValue };
        if (key === 'powerKw') {
            const kw = parseFloat(nextValue);
            next.powerHp = Number.isFinite(kw) ? String(Math.round(kw * 1.34102)) : '';
        }
        onChange(next);
    };

    // The "+ Add …" rows persist into the store's own brand catalogue
    // (optimistic, rolled back on failure by the context) and NEVER block the
    // form — the typed value stays in place whether the save succeeds or not.
    const commitNewModel = (model) => {
        const name = String(model || '').trim();
        if (!name) return;
        setField('model', name);
        if (typeof addModelHandler === 'function') {
            Promise.resolve(addModelHandler(value.brand, name)).catch(() => {});
        }
    };

    const commitNewType = (type) => {
        const name = String(type || '').trim();
        if (!name) return;
        setField('type', name);
        if (typeof addTypeHandler === 'function') {
            Promise.resolve(addTypeHandler(value.brand, value.model, name)).catch(() => {});
        }
    };

    const inputBaseStyle = [
        styles.input,
        {
            color: theme.colors.text,
            backgroundColor: theme.dark ? '#0F172A' : '#FFFFFF',
            borderColor: theme.dark ? 'rgba(148,163,184,0.22)' : '#E6EAF1',
        },
    ];

    return (
        <View style={styles.root}>
            <View style={styles.row}>
                <Field label={tr('repair_brand', 'Marka')} theme={theme}>
                    <View style={[styles.dropdownAnchor, brandFocused && styles.dropdownAnchorActive]}>
                        <TextInput
                            style={inputBaseStyle}
                            value={toNumberString(value.brand)}
                            onChangeText={(text) => setField('brand', text)}
                            onFocus={() => setBrandFocused(true)}
                            onBlur={() => globalThis.setTimeout(() => setBrandFocused(false), 120)}
                            placeholder={tr('repair_brand', 'Marka')}
                            placeholderTextColor={theme.colors.placeholder}
                        />
                        {brandFocused && (filteredBrands.length > 0 || showAddBrandRow) && (
                            <View style={[styles.dropdownMenu, { backgroundColor: theme.dark ? '#16213e' : '#FFFFFF', borderColor: theme.dark ? 'rgba(148,163,184,0.22)' : '#E6EAF1' }]}>
                                <ScrollView nestedScrollEnabled style={{ maxHeight: 180 }} keyboardShouldPersistTaps="handled">
                                    {filteredBrands.map((brand) => (
                                        <TouchableOpacity
                                            key={brand}
                                            style={styles.dropdownRow}
                                            onPress={() => {
                                                onChange({ ...value, brand, model: '' });
                                                setBrandFocused(false);
                                            }}
                                        >
                                            <Icon name="car" size={14} color={BRAND_PRIMARY} />
                                            <Text style={[styles.dropdownText, { color: theme.colors.text }]}>{brand}</Text>
                                        </TouchableOpacity>
                                    ))}
                                    {showAddBrandRow && (
                                        <TouchableOpacity
                                            key="__add_brand__"
                                            style={styles.dropdownRow}
                                            onPress={() => {
                                                const query = String(value.brand || '').trim();
                                                onAddBrand(query);
                                                setBrandFocused(false);
                                            }}
                                        >
                                            <Icon name="plus-circle" size={14} color={BRAND_PRIMARY} />
                                            <Text style={[styles.dropdownText, { color: BRAND_PRIMARY }]}>
                                                {tr('repair_add_brand_quick', "Hozzáadás: '{{name}}'").replace('{{name}}', String(value.brand || '').trim())}
                                            </Text>
                                        </TouchableOpacity>
                                    )}
                                </ScrollView>
                            </View>
                        )}
                    </View>
                </Field>

                <Field label={tr('repair_model', 'Modell')} theme={theme}>
                    <View style={[styles.dropdownAnchor, modelFocused && styles.dropdownAnchorActive]}>
                        <TextInput
                            style={inputBaseStyle}
                            value={toNumberString(value.model)}
                            onChangeText={(text) => setField('model', text)}
                            onFocus={() => setModelFocused(true)}
                            onBlur={() => globalThis.setTimeout(() => setModelFocused(false), 120)}
                            placeholder={tr('repair_model', 'Modell')}
                            placeholderTextColor={theme.colors.placeholder}
                            editable={!!value.brand}
                        />
                        {modelFocused && (filteredModels.length > 0 || showAddModelRow) && !!value.brand && (
                            <View style={[styles.dropdownMenu, { backgroundColor: theme.dark ? '#16213e' : '#FFFFFF', borderColor: theme.dark ? 'rgba(148,163,184,0.22)' : '#E6EAF1' }]}>
                                <ScrollView nestedScrollEnabled style={{ maxHeight: 180 }} keyboardShouldPersistTaps="handled">
                                    {filteredModels.map((model) => (
                                        <TouchableOpacity
                                            key={model}
                                            style={styles.dropdownRow}
                                            onPress={() => {
                                                setField('model', model);
                                                setModelFocused(false);
                                            }}
                                        >
                                            <Icon name="car-info" size={14} color={BRAND_PRIMARY} />
                                            <Text style={[styles.dropdownText, { color: theme.colors.text }]}>{model}</Text>
                                        </TouchableOpacity>
                                    ))}
                                    {showAddModelRow && (
                                        <TouchableOpacity
                                            key="__add_model__"
                                            style={styles.dropdownRow}
                                            onPress={() => {
                                                commitNewModel(value.model);
                                                setModelFocused(false);
                                            }}
                                        >
                                            <Icon name="plus-circle" size={14} color={BRAND_PRIMARY} />
                                            <Text style={[styles.dropdownText, { color: BRAND_PRIMARY }]}>
                                                {tr('repair_add_model_quick', "Hozzáadás: '{{name}}'").replace('{{name}}', String(value.model || '').trim())}
                                            </Text>
                                        </TouchableOpacity>
                                    )}
                                </ScrollView>
                            </View>
                        )}
                    </View>
                </Field>
            </View>

            {(isFieldVisible('type') || isFieldVisible('year')) && (
            <View style={styles.row}>
                {isFieldVisible('type') && (
                <Field label={tr('repair_vehicle_type', 'Tipus')} theme={theme}>
                    <View style={[styles.dropdownAnchor, typeFocused && styles.dropdownAnchorActive]}>
                        <TextInput
                            style={inputBaseStyle}
                            value={toNumberString(value.type)}
                            onChangeText={(text) => setField('type', text)}
                            onFocus={() => setTypeFocused(true)}
                            onBlur={() => globalThis.setTimeout(() => setTypeFocused(false), 120)}
                            placeholder={tr('repair_vehicle_type', 'Tipus')}
                            placeholderTextColor={theme.colors.placeholder}
                        />
                        {typeFocused && (filteredTypes.length > 0 || showAddTypeRow) && (
                            <View style={[styles.dropdownMenu, { backgroundColor: theme.dark ? '#16213e' : '#FFFFFF', borderColor: theme.dark ? 'rgba(148,163,184,0.22)' : '#E6EAF1' }]}>
                                <ScrollView nestedScrollEnabled style={{ maxHeight: 180 }} keyboardShouldPersistTaps="handled">
                                    {filteredTypes.map((type) => (
                                        <TouchableOpacity
                                            key={type}
                                            style={styles.dropdownRow}
                                            onPress={() => {
                                                setField('type', type);
                                                setTypeFocused(false);
                                            }}
                                        >
                                            <Icon name="car-shift-pattern" size={14} color={BRAND_PRIMARY} />
                                            <Text style={[styles.dropdownText, { color: theme.colors.text }]}>{type}</Text>
                                        </TouchableOpacity>
                                    ))}
                                    {showAddTypeRow && (
                                        <TouchableOpacity
                                            key="__add_type__"
                                            style={styles.dropdownRow}
                                            onPress={() => {
                                                commitNewType(value.type);
                                                setTypeFocused(false);
                                            }}
                                        >
                                            <Icon name="plus-circle" size={14} color={BRAND_PRIMARY} />
                                            <Text style={[styles.dropdownText, { color: BRAND_PRIMARY }]}>
                                                {tr('repair_add_type_quick', "Hozzáadás: '{{name}}'").replace('{{name}}', String(value.type || '').trim())}
                                            </Text>
                                        </TouchableOpacity>
                                    )}
                                </ScrollView>
                            </View>
                        )}
                    </View>
                </Field>
                )}
                {isFieldVisible('year') && (
                <Field label={tr('repair_year', 'Evjarat')} theme={theme}>
                    <TextInput
                        style={inputBaseStyle}
                        value={toNumberString(value.year)}
                        onChangeText={(text) => setField('year', text)}
                        placeholder={tr('repair_year', 'Evjarat')}
                        placeholderTextColor={theme.colors.placeholder}
                        keyboardType="numeric"
                    />
                </Field>
                )}
            </View>
            )}

            {isFieldVisible('fuel') && (
            <Field label={tr('repair_fuel', 'Uzemanyag')} theme={theme}>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 8 }}>
                    <View style={styles.fuelRow}>
                        {fuels.map((fuel) => {
                            const active = value.fuel === fuel.value;
                            return (
                                <TouchableOpacity
                                    key={fuel.value}
                                    style={[
                                        styles.fuelChip,
                                        {
                                            borderColor: active ? BRAND_PRIMARY : (theme.dark ? 'rgba(148,163,184,0.22)' : '#E6EAF1'),
                                            backgroundColor: active ? '#EEF3FF' : (theme.dark ? '#0F172A' : '#FFFFFF'),
                                        },
                                    ]}
                                    onPress={() => setField('fuel', active ? '' : fuel.value)}
                                >
                                    <Text style={{ color: active ? BRAND_PRIMARY : theme.colors.subText, fontSize: 12, fontWeight: active ? '700' : '600' }}>
                                        {fuel.label}
                                    </Text>
                                </TouchableOpacity>
                            );
                        })}
                    </View>
                </ScrollView>
            </Field>
            )}

            {(isFieldVisible('engineCode') || isFieldVisible('displacement')) && (
            <View style={styles.row}>
                {isFieldVisible('engineCode') && (
                <Field label={tr('repair_engine_code', 'Motorkod')} theme={theme}>
                    <TextInput
                        style={inputBaseStyle}
                        value={toNumberString(value.engineCode)}
                        onChangeText={(text) => setField('engineCode', text.toUpperCase())}
                        placeholder={tr('repair_engine_code', 'Motorkod')}
                        placeholderTextColor={theme.colors.placeholder}
                    />
                </Field>
                )}
                {isFieldVisible('displacement') && (
                <Field label={tr('repair_displacement', 'Hengerurtartalom (cc)')} theme={theme}>
                    <TextInput
                        style={inputBaseStyle}
                        value={toNumberString(value.displacement)}
                        onChangeText={(text) => setField('displacement', text)}
                        placeholder={tr('repair_displacement', 'Hengerurtartalom (cc)')}
                        placeholderTextColor={theme.colors.placeholder}
                        keyboardType="numeric"
                    />
                </Field>
                )}
            </View>
            )}

            {(isFieldVisible('powerKw') || isFieldVisible('powerHp')) && (
            <View style={styles.row}>
                {isFieldVisible('powerKw') && (
                <Field label={tr('repair_power_kw', 'Teljesitmeny (kW)')} theme={theme}>
                    <TextInput
                        style={inputBaseStyle}
                        value={toNumberString(value.powerKw)}
                        onChangeText={(text) => setField('powerKw', text)}
                        placeholder={tr('repair_power_kw', 'Teljesitmeny (kW)')}
                        placeholderTextColor={theme.colors.placeholder}
                        keyboardType="numeric"
                    />
                </Field>
                )}
                {isFieldVisible('powerHp') && (
                <Field label={tr('repair_power_hp', 'Teljesitmeny (LE)')} theme={theme}>
                    <TextInput
                        style={[
                            ...inputBaseStyle,
                            { backgroundColor: theme.dark ? 'rgba(15,23,42,0.5)' : '#F8FAFC' },
                        ]}
                        value={toNumberString(value.powerHp)}
                        onChangeText={(text) => setField('powerHp', text)}
                        placeholder={tr('repair_power_hp', 'Teljesitmeny (LE)')}
                        placeholderTextColor={theme.colors.placeholder}
                        keyboardType="numeric"
                    />
                </Field>
                )}
            </View>
            )}

            {isFieldVisible('color') && (
            <Field label={tr('repair_color', 'Szin')} theme={theme}>
                <TextInput
                    style={inputBaseStyle}
                    value={toNumberString(value.color)}
                    onChangeText={(text) => setField('color', text)}
                    placeholder={tr('repair_color', 'Szin')}
                    placeholderTextColor={theme.colors.placeholder}
                />
            </Field>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    root: { width: '100%', overflow: 'visible', position: 'relative', zIndex: 1 },
    row: { flexDirection: 'row', gap: 10, overflow: 'visible', position: 'relative', zIndex: 1 },
    field: { flex: 1, marginBottom: 8, overflow: 'visible', position: 'relative', zIndex: 1 },
    label: { fontSize: 11, fontWeight: '800', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.35 },
    input: {
        borderRadius: 12,
        borderWidth: 1,
        height: 48,
        paddingHorizontal: 12,
        fontSize: 16,
        ...(Platform.OS === 'web' ? { outlineWidth: 0, outlineColor: 'transparent', outlineStyle: 'none' } : {}),
    },
    dropdownAnchor: {
        position: 'relative',
        zIndex: 99999990,
        ...(IS_WEB ? { isolation: 'isolate' } : {}),
        overflow: 'visible',
    },
    dropdownAnchorActive: { zIndex: 99999995, elevation: 9998 },
    dropdownMenu: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        marginTop: 6,
        zIndex: 99999999,
        elevation: 9999,
        borderWidth: 1,
        borderRadius: 12,
        overflow: 'hidden',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.12,
        shadowRadius: 9,
    },
    dropdownRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 12, paddingVertical: 10 },
    dropdownText: { fontSize: 14, fontWeight: '600' },
    fuelRow: { flexDirection: 'row', gap: 8, paddingBottom: 2 },
    fuelChip: { borderRadius: 999, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 8 },
});


